import * as icons from "@mui/icons-material";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import AddressPicker from "components/AddressPicker";
import { CustomCkeditor } from "components/CustomCkeditor";
import CustomDatePicker from "components/CustomDatePicker";
import CurrencyFormat from "components/formatter/CurrencyFormat";
import PercentFormat from "components/formatter/PercentFormat";
import { formatArray } from "lib/arrayObject";
import PhoneNumberInput from "components/PhoneNumberInput";
import React from "react";
import { DefaultEditor } from "react-simple-wysiwyg";
import S3MultiFileUpload from "./S3MultiFileUpload";

export default function EditFormControl({
  data = {},
  type = "text",
  name = "",
  label = "",
  placeholder = "",
  onChange = () => {},
  onChangePhoneCode = () => {},
  size = "small",
  fullWidth = false,

  optionLabel = (option) => (typeof option === "string" ? option : option?.id),
  options = [],

  onKeyDown = () => {},

  onBlur = () => {},

  value = "",

  // error
  error = {},

  readOnly = false,

  isLabel = false,

  startIcon = "",

  endIcon = "",

  // for file upload
  destination = "",

  className = "",

  rows = 5,

  ...props
}) {
  type = String(type).toLowerCase();

  // const isError = isRequire && data?.value && !checkValid(type, data?.value);
  const isError = Boolean(error?.[name]);

  const basicProps = {
    name: name ?? "",
    // ...(label ? { label: label } : {}),
    // label: isLabel ? "" : label ?? "",
    placeholder: placeholder,
    size: size ?? "",
    disabled: readOnly,
  };

  const generalProps = {
    ...basicProps,
    size: size ?? "",
    fullWidth: Boolean(fullWidth),
    disabled: readOnly,
  };

  const propsValue = data?.[name ?? ""] ?? value ?? "";

  const textProps = {
    value: propsValue,
    onChange: onChange,
    onKeyDown: onKeyDown,
    onBlur: onBlur,
  };

  const TIcon = icons?.[startIcon] ?? icons?.[endIcon] ?? "";
  const inputProps = startIcon
    ? {
        InputProps: {
          startAdornment: (
            <InputAdornment position="start">
              <TIcon fontSize="large" color="secondary" />
            </InputAdornment>
          ),
        },
      }
    : endIcon
    ? {
        InputProps: {
          endAdornment: (
            <InputAdornment position="start">
              <TIcon fontSize="large" color="secondary" />
            </InputAdornment>
          ),
        },
      }
    : {};
  const errorProps = {
    error: isError,
    helperText: error?.[name],
  };

  const handleSelectChange = (e = {}, value = {}) => {
    onChange({ target: { name: name, value: value } });
  };

  const handleRtfChange = (e = {}) => {
    onChange({ target: { name: name, value: e?.target?.value } });
  };

  const handleDataChange = (value = "") => {
    onChange({ target: { name: name, value: value } });
  };

  const handleCheck = (e) => {
    onChange({ target: { name: name, value: Boolean(e?.target?.checked) } });
  };

  return (
    <div className={`${className}`}>
      {type === "date" ? (
        <CustomDatePicker
          {...generalProps}
          {...errorProps}
          value={propsValue ?? ""}
          onChange={handleDataChange}
        />
      ) : type === "select" ? (
        <Autocomplete
          // disablePortal
          options={options}
          disableClearable
          renderInput={(params) => (
            <TextField
              {...params}
              // {...(label ? { label: label } : {})}
              {...errorProps}
              {...inputProps}
            />
          )}
          {...generalProps}
          getOptionLabel={optionLabel}
          value={propsValue ?? null}
          onChange={handleSelectChange}
        />
      ) : type === "rtf" ? (
        <div>
          <DefaultEditor
            {...basicProps}
            value={propsValue ?? ""}
            onChange={handleRtfChange}
            className={`${isError ? "border rounded border-error" : ""}`}
          />
          {isError ? (
            <FormHelperText error>{errorProps.helperText}</FormHelperText>
          ) : null}
        </div>
      ) : type === "ckeditor" ? (
        <CustomCkeditor
          value={propsValue}
          onChange={onChange}
          {...generalProps}
        />
      ) : type === "switch" ? (
        isLabel ? (
          <Switch
            {...basicProps}
            checked={Boolean(propsValue)}
            onChange={handleCheck}
            size={size}
            disabled={readOnly}
          />
        ) : (
          <FormControlLabel
            label={label}
            control={
              <Switch
                {...basicProps}
                checked={Boolean(propsValue)}
                onChange={handleCheck}
              />
            }
          />
        )
      ) : type === "checkbox" ? (
        isLabel ? (
          <Checkbox
            {...basicProps}
            checked={Boolean(propsValue)}
            onChange={handleCheck}
            size={size}
            disabled={readOnly}
          />
        ) : (
          <FormControlLabel
            label={label}
            control={
              <Checkbox
                {...basicProps}
                checked={Boolean(propsValue)}
                onChange={handleCheck}
              />
            }
          />
        )
      ) : type === "file" ? (
        <div>
          <S3MultiFileUpload
            destination={destination}
            files={propsValue ? [propsValue] : []}
            onChange={(v) => handleDataChange(formatArray(v)?.[0] ?? "")}
            className={`${isError ? "border rounded border-error" : ""}`}
          />
          {isError ? (
            <FormHelperText error>{errorProps.helperText}</FormHelperText>
          ) : null}
        </div>
      ) : type === "files" ? (
        <div>
          <S3MultiFileUpload
            allowMultiple
            destination={destination}
            files={propsValue}
            onChange={handleDataChange}
            className={`${isError ? "border rounded border-error" : ""}`}
          />
          {isError ? (
            <FormHelperText error>{errorProps.helperText}</FormHelperText>
          ) : null}
        </div>
      ) : type === "money" ? (
        <TextField
          {...generalProps}
          {...textProps}
          {...errorProps}
          InputProps={{
            inputComponent: CurrencyFormat,
          }}
        />
      ) : type === "percent" ? (
        <TextField
          {...generalProps}
          {...textProps}
          {...errorProps}
          InputProps={{
            inputComponent: PercentFormat,
          }}
        />
      ) : type === "address" ? (
        <AddressPicker
          {...generalProps}
          value={propsValue ?? ""}
          onChange={handleSelectChange}
          {...errorProps}
          {...inputProps}
        />
      ) : type === "phone" ? (
        <TextField
          type="text"
          {...generalProps}
          {...textProps}
          {...errorProps}
          {...inputProps}
        />
      ) : type === "multiline" ? (
        <TextField
          type="text"
          {...generalProps}
          {...textProps}
          {...errorProps}
          {...inputProps}
          multiline
          rows={rows}
        />
      ) : type === "native_select" ? (
        <div>
          <Select error={isError} {...textProps} {...generalProps}>
            {options.map((item, itemIndex) => (
              <MenuItem value={item?.id} key={itemIndex}>
                {item?.label ?? ""}
              </MenuItem>
            ))}
          </Select>
          {isError ? (
            <FormHelperText error>
              {errorProps?.helperText ?? ""}
            </FormHelperText>
          ) : null}
        </div>
      ) : type === "phone_input" ? (
        <PhoneNumberInput
          phone_code={data?.phone_code ?? "64"}
          onChangePhoneCode={onChangePhoneCode}
          {...generalProps}
          {...textProps}
          {...errorProps}
          {...inputProps}
        />
      ) : (
        // number, text, password, email,
        <TextField
          type={type}
          {...generalProps}
          {...textProps}
          {...errorProps}
          {...inputProps}
        />
      )}
    </div>
  );
}
